import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Search, X } from 'lucide-react';
import type { ProposalBuilderData } from '../../types';
import { usePairedIframeScrollSync } from '../../hooks/usePairedScrollSync';
import { useCompareSyncHotkey } from '../../hooks/useCompareSyncHotkey';
import '../pm/pm-shared.css';
import './PublicProposalCompareModal.css';

export type PublicProposalCompareItem = ProposalBuilderData & { id: string };

function statusLabel(status?: string): string {
  switch (status) {
    case 'approved':
      return 'Approved';
    case 'sent':
      return 'Sent';
    case 'opened':
    case 'viewed':
      return 'Opened';
    default:
      return 'Draft';
  }
}

function statusPill(status?: string): string {
  if (status === 'approved') return 'completed';
  if (status === 'sent') return 'in-progress';
  if (status === 'opened' || status === 'viewed') return 'review';
  return 'pending';
}

function matchesQuery(proposal: PublicProposalCompareItem, needle: string): boolean {
  if (!needle) return true;
  return (
    (proposal.clientName || '').toLowerCase().includes(needle) ||
    (proposal.slug || '').toLowerCase().includes(needle) ||
    (proposal.projectName || '').toLowerCase().includes(needle)
  );
}

function ProposalSidePicker({
  label,
  proposals,
  excludeId,
  excludeSlug = null,
  selectedId,
  onSelect,
}: {
  label: string;
  proposals: PublicProposalCompareItem[];
  excludeId: string | null;
  excludeSlug?: string | null;
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  const [query, setQuery] = useState('');
  const needle = query.trim().toLowerCase();
  const options = useMemo(
    () =>
      proposals.filter(p => {
        if (p.id === excludeId) return false;
        if (excludeSlug && p.slug === excludeSlug) return false;
        if (!p.slug?.trim()) return false;
        return matchesQuery(p, needle);
      }),
    [proposals, excludeId, excludeSlug, needle],
  );
  const selected = proposals.find(p => p.id === selectedId) || null;

  return (
    <div className={`ppc-picker${selected ? ' is-selected' : ''}`}>
      <div className="ppc-picker-toolbar">
        <div className="ppc-picker-label">{label}</div>
        {selected ? (
          <div className="ppc-picker-selected">
            <div className="ppc-picker-selected-copy">
              <strong>{selected.clientName || selected.projectName || 'Unnamed client'}</strong>
              <span>{[selected.projectName, selected.slug].filter(Boolean).join(' · ')}</span>
            </div>
            <span className="pm-status-pill" data-status={statusPill(selected.status)}>
              {statusLabel(selected.status)}
            </span>
            <button type="button" className="ppc-picker-change" onClick={() => onSelect('')}>
              Change
            </button>
          </div>
        ) : (
          <div className="ppc-picker-search">
            <Search size={13} />
            <input
              type="search"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search client, project, or slug…"
              aria-label={`${label} project search`}
            />
          </div>
        )}
      </div>
      {!selected && (
        <div className="ppc-picker-list" role="listbox" aria-label={`${label} projects`}>
          <div className="ppc-picker-cols" aria-hidden="true">
            <span>Client</span>
            <span>Project</span>
            <span>Status</span>
          </div>
          {options.length === 0 ? (
            <div className="ppc-picker-empty">No projects with a public page</div>
          ) : (
            options.map(p => (
              <button
                key={p.id}
                type="button"
                role="option"
                className="ppc-row"
                onClick={() => onSelect(p.id)}
              >
                <span className="ppc-row-name">{p.clientName || p.projectName || 'Unnamed client'}</span>
                <span className="ppc-row-slug">{p.slug}</span>
                <span className="pm-status-pill" data-status={statusPill(p.status)}>
                  {statusLabel(p.status)}
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function resolveCompareId(
  proposals: PublicProposalCompareItem[],
  needle: string | null | undefined,
): string | null {
  if (!needle) return null;
  const match = proposals.find(p => p.id === needle || p.dealId === needle || p.slug === needle);
  return match?.id || null;
}

export default function PublicProposalCompareModal({
  open,
  proposals,
  initialLeftId,
  onClose,
}: {
  open: boolean;
  proposals: PublicProposalCompareItem[];
  initialLeftId?: string | null;
  onClose: () => void;
}) {
  const [leftId, setLeftId] = useState<string | null>(null);
  const [rightId, setRightId] = useState<string | null>(null);
  const [syncOn, setSyncOn] = useState(false);
  const leftRef = useRef<HTMLIFrameElement>(null);
  const rightRef = useRef<HTMLIFrameElement>(null);

  const left = proposals.find(p => p.id === leftId) || null;
  const right = proposals.find(p => p.id === rightId) || null;
  const leftSrc = left?.slug ? `/proposal/${left.slug}?from=portal&portalSlug=preview` : '';
  const rightSrc = right?.slug ? `/proposal/${right.slug}?from=portal&portalSlug=preview` : '';

  usePairedIframeScrollSync(
    leftRef,
    rightRef,
    open && syncOn && !!leftSrc && !!rightSrc,
    `${leftSrc}|${rightSrc}`,
  );
  useCompareSyncHotkey(
    open && !!leftSrc && !!rightSrc,
    () => setSyncOn(on => !on),
    leftRef,
    rightRef,
    `${leftSrc}|${rightSrc}`,
  );

  useEffect(() => {
    if (!open) {
      setLeftId(null);
      setRightId(null);
      setSyncOn(false);
      return;
    }
    const nextLeft = resolveCompareId(proposals, initialLeftId);
    if (nextLeft) setLeftId(nextLeft);
  }, [open, initialLeftId, proposals]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div className="ppc-overlay" role="dialog" aria-modal="true" aria-labelledby="ppc-title">
      <div className="ppc-shell">
        <div className="ppc-bar">
          <h2 id="ppc-title" className="ppc-title">Compare public pages</h2>
          <div className="ppc-bar-actions">
            <button
              type="button"
              className={`ppc-sync-btn${syncOn ? ' is-on' : ''}`}
              onClick={() => setSyncOn(on => !on)}
              disabled={!leftSrc || !rightSrc}
              title={
                syncOn
                  ? 'Scroll sync is on — both pages move together (S)'
                  : 'Turn on scroll sync (S)'
              }
            >
              {syncOn ? 'Sync on' : 'Sync off'}
            </button>
            <button type="button" className="ppc-close" onClick={onClose} aria-label="Close compare">
              <X size={16} />
              Close
            </button>
          </div>
        </div>
        <div className="ppc-panes">
          <div className="ppc-pane">
            <ProposalSidePicker
              label="Left"
              proposals={proposals}
              excludeId={rightId}
              excludeSlug={right?.slug || null}
              selectedId={leftId}
              onSelect={id => setLeftId(id || null)}
            />
            {leftSrc ? (
              <iframe
                key={leftSrc}
                ref={leftRef}
                className="ppc-frame"
                title={`${left?.clientName || 'Proposal'} public page`}
                src={leftSrc}
              />
            ) : null}
          </div>
          <div className="ppc-pane">
            <ProposalSidePicker
              label="Right"
              proposals={proposals}
              excludeId={leftId}
              excludeSlug={left?.slug || null}
              selectedId={rightId}
              onSelect={id => setRightId(id || null)}
            />
            {rightSrc ? (
              <iframe
                key={rightSrc}
                ref={rightRef}
                className="ppc-frame"
                title={`${right?.clientName || 'Proposal'} public page`}
                src={rightSrc}
              />
            ) : null}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
